/**
 * @file search-button.tsx
 */

"use client";

import * as React from "react";
import { IconZoomCodeFilled } from "@tabler/icons-react";
import { Button, cn, buttonVariants } from "visuais";
import { type VariantProps } from "class-variance-authority";

/**
 * @returns {JSX.Element} Propriedades do botão de busca.
 * @example
 * ```tsx
 * <ShiningButton
 *   onClick={() => console.log("Buscar")}
 *   label="Buscar Produto"
 *   showShineEffect={true}
 * />
 * ```
 */
export interface ShiningButtonProps
  extends React.ComponentProps<"button">,
    VariantProps<typeof buttonVariants> {
  /** Ícone personalizado para o botão */
  icon?: React.ReactNode;
  /** Texto do botão */
  label?: string;
  /** Se deve mostrar o efeito de brilho */
  showShineEffect?: boolean;
  /** Se deve mostrar o label */
  showLabel?: boolean;
  /** Classe CSS personalizada para o botão */
  buttonClassName?: string;
}

/**
 * @returns {JSX.Element} Botão de busca com efeito de brilho.
 * @example
 * ```tsx
 * <ShiningButton
 *   onClick={() => handleSearch()}
 *   label="Buscar Produto"
 *   showShineEffect={true}
 *   variant="outline"
 *   size="default"
 * />
 * ```
 */
export function ShiningButton({
  icon,
  label = "Buscar",
  showShineEffect = false,
  showLabel = true,
  buttonClassName,
  className,
  size = "default",
  variant = "outline",
  disabled = false,
  onClick,
  ...props
}: ShiningButtonProps) {
  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      console.log("🔍 [ShiningButton] Botão clicado!");
      onClick?.(e);
    },
    [onClick]
  );

  return (
    <Button
      type="button"
      size={size}
      variant={variant}
      onClick={handleClick}
      disabled={disabled}
      title={label}
      className={cn(
        "group relative overflow-hidden",
        buttonClassName || className
      )}
      aria-label={label}
      {...props}
    >
      {/* Shine effect - controlado por showShineEffect */}
      {showShineEffect && (
        <div className="absolute inset-0 -top-1 -left-1 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12 translate-x-[-100%] group-hover:translate-x-[200%] transition-transform duration-1000 ease-out" />
      )}

      {/* Content */}
      <div className="relative flex items-center gap-2">
        {icon || <IconZoomCodeFilled className="h-4 w-4" />}
        {showLabel && <span className="tracking-wide">{label}</span>}
      </div>
    </Button>
  );
}
